import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Sliders } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import {
  SANDBOX_INTERVAL_DEFAULT_SEC,
  SANDBOX_INTERVAL_MAX_SEC,
  SANDBOX_INTERVAL_MIN_SEC,
} from '@/data/missions';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';

interface SandboxSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Sandbox-only configuration. Currently exposes the mission spawn interval
 * slider (5–300 s). New sandbox-only knobs slot in here as Phase 5/6 evolves.
 */
export function SandboxSettingsModal({ open, onClose }: SandboxSettingsModalProps) {
  const intervalSec = useGameStore((s) => s.sandboxConfig.missionIntervalSec);
  const setIntervalSec = useGameStore((s) => s.setSandboxMissionInterval);
  const policeShare = useGameStore((s) => s.sandboxConfig.policeMissionSharePct ?? 30);
  const setPoliceShare = useGameStore((s) => s.setSandboxPoliceShare);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <ModalRoot
          onClose={onClose}
          zIndex={Z_MODALS}
          contentClass="w-full max-w-md rounded-lg border border-border-strong bg-bg-secondary p-6"
        >
            <div className="mb-4 flex items-start justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-accent-dispatch/15 text-accent-dispatch">
                  <Sliders className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-accent-dispatch">
                    Sandbox
                  </p>
                  <h3 id="sandbox-settings-title" className="font-display text-lg font-semibold">
                    Einstellungen
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Schließen"
                className="text-fg-muted transition hover:text-fg-primary"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <label htmlFor="sandbox-interval" className="text-sm font-semibold">
                  Notruf-Intervall
                </label>
                <span className="font-display text-lg font-bold tabular-nums text-accent-dispatch">
                  {intervalSec} s
                </span>
              </div>
              <input
                id="sandbox-interval"
                type="range"
                min={SANDBOX_INTERVAL_MIN_SEC}
                max={SANDBOX_INTERVAL_MAX_SEC}
                step={1}
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                className="w-full accent-accent-dispatch"
              />
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-fg-muted">
                <span>{SANDBOX_INTERVAL_MIN_SEC} s (hektisch)</span>
                <span>Default {SANDBOX_INTERVAL_DEFAULT_SEC} s</span>
                <span>{SANDBOX_INTERVAL_MAX_SEC} s (entspannt)</span>
              </div>
              <p className="mt-3 text-xs text-fg-muted">
                Wert in Echtzeit-Sekunden bei 1× Spielgeschwindigkeit. Wirkt sofort auf den
                nächsten Spawn.
              </p>
            </div>

            {/* Phase 10.7.3 — Polizei-Mission-Anteil */}
            <div className="mt-6 space-y-2">
              <div className="flex items-baseline justify-between">
                <label htmlFor="sandbox-police-share" className="text-sm font-semibold">
                  Polizei-Mission-Anteil
                </label>
                <span className="font-display text-lg font-bold tabular-nums text-blue-400">
                  {policeShare} %
                </span>
              </div>
              <input
                id="sandbox-police-share"
                type="range"
                min={0}
                max={100}
                step={5}
                value={policeShare}
                onChange={(e) => setPoliceShare(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-fg-muted">
                <span>0 % (nur Feuer/Rettung)</span>
                <span>30 % (default)</span>
                <span>100 % (nur Polizei)</span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-accent-dispatch px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-dispatch-hover"
              >
                Fertig
              </button>
            </div>
        </ModalRoot>
      )}
    </AnimatePresence>
  );
}
